import React, { useMemo, useState } from 'react';
import {
  Box, Typography, MenuItem, Select, FormControl, FormHelperText, IconButton, Alert, Paper, TextField, InputAdornment,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import MediaPreviewOverlay from '../common/MediaPreviewOverlay';
import { selectFieldSx, dropdownMenuProps, textFieldSx } from '../../utils/dropdownStyles';

// The first preview link of a model, preferring English. The registry is managed from Postman, so a model may have none.
export const modelPreviewUrl = (model) => {
  const links = (model?.previewMedia?.linkLocalizations ?? []).filter((l) => (l.mediaLink ?? '').trim());
  return (links.find((l) => l.localeId === 'en_us') ?? links[0])?.mediaLink ?? '';
};

// The selected-model card is LOCKED information, not a control: its fields are disabled (no hover, no focus, no cursor), tinted
// so they do not look like the white, clickable model dropdown above them, with plain black text (not the faded disabled grey).
const lockedFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#eaf0f7',
    cursor: 'default',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d5e0ec' },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#d5e0ec' },
  },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#1a1a1a', color: '#1a1a1a', cursor: 'default' },
  '& .MuiInputLabel-root.Mui-disabled': { color: '#5a6b7d' },
};

const Thumb = ({ url, size = 36 }) =>
  url ? (
    <Box
      component="img"
      src={url}
      alt=""
      sx={{ width: size, height: size, objectFit: 'cover', borderRadius: '6px', bgcolor: '#EAF0F6', flexShrink: 0 }}
    />
  ) : (
    <Box sx={{ width: size, height: size, borderRadius: '6px', bgcolor: '#EAF0F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <ViewInArIcon sx={{ fontSize: size * 0.55, color: '#85a5c6' }} />
    </Box>
  );

/**
 * The 3D model of a 3D ad, picked from the space's model registry (managed from Postman; the CMS only reads it).
 *
 * models         every model of the space, inactive ones included
 * value          the selected model id ('' = none)
 * currentModelId the ad's stored model: it stays selectable (and visible) even if it was deactivated later, so the ad can still be edited
 * currentModel   the ad's stored model reference { id, modelKey, displayName }: its name is shown when the registry did not return it
 * loadFailed     the model list could not be loaded (not the same as "the space has no models")
 * An inactive model is never offered for a NEW pick.
 */
const AdModelPicker = ({ models = [], value = '', onChange, disabled = false, currentModelId = null, currentModel = null, loading = false, loadFailed = false, required = false }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const nameLabel        = useLocalText('Model Name');
  const descriptionLabel = useLocalText('Description');
  const previewLabel     = useLocalText('Preview Image URL');
  const noPreviewText    = useLocalText('This model has no preview image');
  const inactiveText     = useLocalText('inactive');

  // active models, plus the ad's own model whatever its state
  const selectable = useMemo(
    () => models.filter((m) => m.isActive || m.id === currentModelId),
    [models, currentModelId],
  );
  const selected = models.find((m) => m.id === value) ?? null;
  const previewUrl = modelPreviewUrl(selected);
  const showEmptyState = !loading && !loadFailed && selectable.length === 0 && value === '';
  // an inactive model that is not the ad's own (e.g. the copy of an ad whose model was deactivated) cannot be saved
  const inactiveChosen = Boolean(selected) && !selected.isActive && selected.id !== currentModelId;

  return (
    <Box data-testid="ad-model-picker">
      {loadFailed && (
        <Alert severity="warning" data-testid="ad-model-load-failed" sx={{ mb: 1, fontSize: 12 }}>
          <LocalText>The 3D models could not be loaded. Close this window and open it again to retry.</LocalText>
        </Alert>
      )}
      {showEmptyState && (
        <Alert severity="info" data-testid="ad-model-empty" sx={{ mb: 1, fontSize: 12 }}>
          <LocalText>This space has no active 3D models, so a 3D ad can't be created here. Models are added by an administrator.</LocalText>
        </Alert>
      )}

      <FormControl fullWidth size="small">
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          disabled={disabled || (selectable.length === 0 && value === '')}
          MenuProps={dropdownMenuProps}
          SelectDisplayProps={{ 'data-testid': 'ad-model-select', 'aria-label': '3D Model' }}
          renderValue={(v) => {
            if (v === '' || v == null) {
              return <span style={{ color: '#85a5c6' }}>{loading ? <LocalText>Loading models…</LocalText> : <LocalText>Select a 3D model</LocalText>}</span>;
            }
            const m = models.find((x) => x.id === v);
            return <Typography variant="body2" sx={{ fontSize: 14 }}>{m ? m.displayName : (currentModel?.id === v ? currentModel.displayName : `#${v}`)}</Typography>;
          }}
          sx={selectFieldSx}
        >
          {value !== '' && !selectable.some((m) => m.id === value) && <MenuItem value={value} sx={{ display: 'none' }} />}
          {selectable.map((m) => (
            <MenuItem key={m.id} value={m.id} sx={{ py: 1 }} data-testid={`ad-model-option-${m.id}`}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <Thumb url={modelPreviewUrl(m)} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a5f', fontSize: 13 }}>
                    {m.displayName}
                    {!m.isActive && <span style={{ color: '#b45309', fontWeight: 500 }}>{' '}(<LocalText>inactive</LocalText>)</span>}
                  </Typography>
                  {m.description && (
                    <Typography variant="caption" noWrap sx={{ color: '#85a5c6', fontSize: 10, display: 'block', maxWidth: 280 }}>{m.description}</Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {required && !disabled && value === '' && !showEmptyState && !loadFailed && <FormHelperText><LocalText>Required</LocalText></FormHelperText>}
        {inactiveChosen && !disabled && (
          <FormHelperText error data-testid="ad-model-inactive"><LocalText>This model is inactive. Choose another model.</LocalText></FormHelperText>
        )}
      </FormControl>

      {selected && (
        <Paper
          variant="outlined"
          data-testid="ad-model-summary"
          sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', bgcolor: '#f4f7fb', borderColor: '#e1e9f2', display: 'flex', flexDirection: 'column', gap: 1.25 }}
        >
          <Typography variant="caption" data-testid="ad-model-details-title" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#85a5c6', fontSize: 10 }}>
            <LocalText>Model details</LocalText>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Thumb url={previewUrl} size={56} />
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                disabled
                label={nameLabel}
                value={selected.isActive ? selected.displayName : `${selected.displayName} (${inactiveText})`}
                size="small"
                fullWidth
                sx={lockedFieldSx}
                inputProps={{ 'data-testid': 'ad-model-name' }}
              />
              {selected.description && (
                <TextField
                  disabled
                  label={descriptionLabel}
                  value={selected.description}
                  size="small"
                  fullWidth
                  multiline
                  maxRows={3}
                  sx={lockedFieldSx}
                  inputProps={{ 'data-testid': 'ad-model-description' }}
                />
              )}
              <TextField
                disabled
                label={previewLabel}
                value={previewUrl}
                helperText={previewUrl ? '' : noPreviewText}
                size="small"
                fullWidth
                sx={lockedFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Preview model image"
                        disabled={!previewUrl}
                        onClick={(e) => { e.currentTarget.blur(); setPreviewOpen(true); }}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ 'data-testid': 'ad-model-preview-url' }}
              />
            </Box>
          </Box>
        </Paper>
      )}

      {/* the model exists in the ad but the registry did not return it (e.g. the list failed to load) */}
      {!selected && value !== '' && (
        <Typography variant="caption" data-testid="ad-model-unknown" sx={{ color: '#85a5c6', fontSize: 11, display: 'block', mt: 0.5 }}>
          <LocalText>This model is not in the list right now.</LocalText>
        </Typography>
      )}

      <MediaPreviewOverlay open={previewOpen} onClose={() => setPreviewOpen(false)} type="image" url={previewUrl} />
    </Box>
  );
};

export default AdModelPicker;
